import { Button } from "@/app/common/common-components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/app/common/common-components/ui/Dialog";
import { Input } from "@/app/common/common-components/ui/Input";
import { ClipboardCopy, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";
import QRCode from "react-qr-code";
import HelpLinkIcon from "@/app/common/common-components/HelpLinkIcon";
import apiAgent from "@/app/api/apiAgent";
import { useMutation } from "@tanstack/react-query";
import { Link } from "@/app/common/common-components/ui/Link";

type SharePropertyDialogProps = {
  propertyID: string;
  shared: boolean;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onShareChange: (shared: boolean) => void;
};

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
};

const SharePropertyDialog = ({
  propertyID,
  shared,
  isOpen,
  onOpenChange,
  onShareChange,
}: SharePropertyDialogProps) => {
  const url = `${window.location.origin}/shared/${propertyID || ""}`;

  const { mutate: savePropertyIsPublicListing } = useMutation({
    mutationFn: (isPublicListing: boolean) =>
      apiAgent.Property.SavePropertyIsPublicListing(
        propertyID,
        isPublicListing
      ),
  });

  const handleShareChange = (shared: boolean) => {
    savePropertyIsPublicListing(shared);
    onShareChange(shared);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="tw-my-0">
            Share Listing{" "}
            <HelpLinkIcon href="https://help.entegral.net/en/articles/12505646-share-a-property" />
          </DialogTitle>
          <DialogDescription>
            Share this listing without making it public on portals
          </DialogDescription>
        </DialogHeader>
        <div className="tw-flex tw-flex-col tw-gap-4">
          {shared && (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <p>
                Copy the link below or scan the QR code to share the listing.
              </p>
              <div className="tw-mx-auto tw-rounded tw-border tw-p-2">
                <QRCode value={url || ""} className="size-44" />
              </div>
              <div
                className="tw-flex tw-items-center tw-gap-2"
                onClick={() => copyToClipboard(url)}
              >
                <Input
                  value={url}
                  className="disabled:tw-cursor-pointer"
                  readOnly
                />
                <Button variant="outline" size="icon">
                  <ClipboardCopy className="tw-size-4" />
                </Button>
              </div>
              <Link
                to={url}
                target="_blank"
                className="tw-mx-auto tw-flex tw-items-center tw-gap-1"
              >
                Preview <ExternalLink className="tw-size-4" />
              </Link>
              <div className="tw-mt-2 tw-flex tw-items-center tw-justify-center tw-gap-2">
                Want to stop sharing?{" "}
                <Button
                  variant="destructive"
                  onClick={() => handleShareChange(false)}
                >
                  Stop Sharing
                </Button>
              </div>
            </div>
          )}
          {!shared && (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
              <p>
                Click the button below to share the listing, you will receive a
                private link which you can send to someone.
              </p>
              <Button onClick={() => handleShareChange(true)}>Share</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SharePropertyDialog;
